import moment from "moment";

import { HyperLinkRedirectControl } from "@/Common/Components/Buttons/HyperLinkRedirectControl";
import { CommonTableHeaderComponent } from "@/Common/Components/Tables/CommonTableHeaderComponent";
import { MyleTable } from "@/Common/Components/Tables/MyleTable";
import { MyleTableColumn, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { MyleTableColumnOrderControlv2 } from "@/Common/Components/Tables/MyleTableColumnOrderControlv2";
import { SimplePagerControl } from "@/Common/Components/Tables/SimplePagerControl";
import { EnvironmentTax } from "@/Common/Helpers/EnvironmentTax";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { PrivateBill_BilledTo, RedirectTo } from "@/Common/Models/UI/Enums";
import { ColumnConfigurationModel } from "@/Common/Models/UI/Tables/ColumnConfigurationModel";
import { ITableRenderOptions } from "@/Common/Models/UI/Tables/ITableRenderOptions";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { SalesInboxTableItem } from "../../Models/UI/BillingInboxTableItem";
import { ISalesInboxModel } from "../../Models/UI/ISalesInboxModel";
import { SalesSettingsContainer } from "./SalesSettingsContainer";

import "./SalesInbox.css";

interface ISalesInboxProps {
    Model: ISalesInboxModel;
}

class SalesInbox extends React.Component<ISalesInboxProps, never> {
    private helper: MyleHelper = new MyleHelper();

    public render(): JSX.Element {
        const saveOptionKey = "SalesInboxSettings_OptimizedV2"; // OH YES I DID

        const columnsOptions = this.helper.loadTableColumnOptionsFromSettings(saveOptionKey, this.GetMyleTableColumnsDefaultOptions());
        const tableColumns = this.getTableColumns(columnsOptions);
        const tableRenderOptions = this.loadTableRenderOptions(columnsOptions);

        var cfgm: ColumnConfigurationModel[] = this.helper.castColumnConfigurationToMyleTableColumnOptions(columnsOptions.toArray());

        return (
            <div className="workspace sales-inbox">
                <CommonTableHeaderComponent
                    title={cacheStore.GetString("MySalesLabel")}
                    searchText={this.props.Model.get("GlobalSalesSearchText")}
                    searchPlaceholder={this.props.Model.get("GlobalSalesSearchPlaceholder")}
                    onSearch={text => sessionStore.dispatch(LocalOperations.Billing.Sales_SearchBoxTextChanged, { value: text })}
                    onRefreshClick={() => sessionStore.dispatch(LocalOperations.Billing.Sales_Refresh_ButtonClick)}
                    onSettingsClick={() => sessionStore.dispatch(LocalOperations.Billing.Sales_SearchSettings_ShowOptionsButtonClick)}
                    onPrintClick={async () => await this.onPrintReport(tableColumns)}
                />
                <div id="posref" className="tb ws-scrollable">
                    {this.props.Model.get("ShowSalesDisplaySettings")
                        && <SalesSettingsContainer className="filter-component transaction" Model={this.props.Model} />
                    }
                    <div className="myle-doc-tb main-content table-wrapper">
                        <MyleTable
                            TableRenderOptions={tableRenderOptions}
                            TableId="NotesInbox_main_tb"
                            Columns={tableColumns}
                            DataSource={this.props.Model.get("TableData")}
                            ColumnsPreferencesChangedOperation={ServerOperations.Server.Myle_UpdateUserSettings}
                            RowClickOperation={LocalOperations.Labs.Table_InboxRowClick}
                            SaveOptionskeyTarget={saveOptionKey}
                            TableDataCy="billingInbox-table"
                        />
                    </div>
                </div>
                <div className="ws-footer table-pager-footer">
                    <div className="row">
                        <div className="col-lg-7 col-md-7 col-sm-7">
                            <SimplePagerControl
                                PageSize={this.props.Model.get("SelectedPageSizeOption")}
                                TotalRows={this.props.Model.get("TotalQueryRows")}
                                CurrentPage={this.props.Model.get("CurrentPage")}
                                EventToDispatchOnPageChange={LocalOperations.Billing.Sales_Search_CurrentPageChange}
                            />
                        </div>
                    </div>
                </div>
                {this.props.Model.get("ColumnOrderingPopupOpen") && (
                    <MyleTableColumnOrderControlv2
                        TableColumns={cfgm}
                        SaveOptionskeyTarget={saveOptionKey}
                        ContainerId="columnSettingsPopup"
                        CloseColumOrder={LocalOperations.Billing.Sales_SearchSettings_ColumnOrderingPopup_Close}
                    />
                )}
            </div>
        );
    }

    private GetMyleTableColumnsDefaultOptions(): MyleTableColumnOptions[] {
        const columns = [
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("CreationDateLabel"),
                FieldName: "SaleDate",
                WidthPercentageDefault: 6
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("NumberLabel"),
                FieldName: "SaleNumber",
                WidthPercentageDefault: 6
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("ProviderLabel"),
                FieldName: "SaleProvider",
                WidthPercentageDefault: 6
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("PatientLabel"),
                FieldName: "PatientName",
                WidthPercentageDefault: 6,
                RowClickEnabled: false
            }),
            new MyleTableColumnOptions({
                HeaderText: sessionStore.getDefaultHealthCardLabel(),
                FieldName: "NAM",
                WidthPercentageDefault: 6,
                RowClickEnabled: false
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("PaymentMethodLabel"),
                FieldName: "PaymentMethod",
                WidthPercentageDefault: 6
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("BilledByLabel"),
                FieldName: "BilledBy",
                WidthPercentageDefault: 6
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("ServiceIDLabel"),
                FieldName: "ServiceID",
                WidthPercentageDefault: 6
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("DescriptionLabel"),
                FieldName: "Description",
                WidthPercentageDefault: 6
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("QuantityLabel"),
                FieldName: "Quantity",
                WidthPercentageDefault: 6
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("UnitPriceLabel"),
                FieldName: "Price",
                WidthPercentageDefault: 6
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("TotalLabel"),
                FieldName: "Total",
                WidthPercentageDefault: 6
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("VisitDateLabel"),
                FieldName: "VisitDate",
                WidthPercentageDefault: 6,
                RowClickEnabled: true
            })
        ];

        if (cacheStore.ClinicSettings.EnablePrivateBillRecipient) {
            columns.push(new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("BilledToLabel"),
                FieldName: "BilledTo",
                WidthPercentageDefault: 6,
                RowClickEnabled: true,
                IsHidden: true
            }));
        }

        if (this.shouldShowGst()) {
            columns.push(new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("GstMLabel"),
                FieldName: "GST",
                WidthPercentageDefault: 6
            }));
        }

        if (this.shouldShowPstOrQst()) {
            columns.push(new MyleTableColumnOptions({
                HeaderText: EnvironmentTax.getPstOrQstLabel(),
                FieldName: "QST",
                WidthPercentageDefault: 6
            }));
        }

        if (this.shouldShowHst()) {
            columns.push(new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("HstMLabel"),
                FieldName: "HST",
                WidthPercentageDefault: 6
            }));
        }

        if (this.shouldShowGenericTax()) {
            columns.push(new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("TaxLabel"),
                FieldName: "GenericTax",
                WidthPercentageDefault: 6
            }));
        }

        return columns;
    }

    private getTableColumns(columnsOptions: Immutable.List<MyleTableColumnOptions>): Immutable.List<MyleTableColumn> {
        return MyleTableColumn.buildList<SalesInboxTableItem>(
            columnsOptions,
            column => {
                switch (column.FieldName) {
                    case "SaleDate":
                        return {
                            renderCell: item => {
                                var date = moment(item.Model.SaleDate).format("YYYY/MM/DD");
                                var time = moment(item.Model.SaleDate).format("HH:mm:ss A");
                                var messageDate = date + " " + cacheStore.GetString("AtLabel") + " " + time;

                                if (item.Model.IsInvalid)
                                    return (<div style={{ color: "red" }}>{messageDate}</div>);
                                else
                                    return (<div>{messageDate}</div>);
                            }
                        };

                    case "VisitDate":
                        return {
                            renderCell: item => {
                                var date = moment(item.Model.VisitDate).format("YYYY/MM/DD");

                                if (item.Model.IsInvalid)
                                    return (<div style={{ color: "red" }}>{date}</div>);
                                else
                                    return (<div>{date}</div>);
                            }
                        };

                    case "SaleNumber":
                        return {
                            renderCell: item => {
                                if (item.Model.IsInvalid)
                                    return (<div style={{ color: "red" }}>{item.Model.SaleNumber}</div>);
                                else
                                    return (<div>{item.Model.SaleNumber}</div>);
                            }
                        };

                    case "SaleProvider":
                        return {
                            renderCell: item => {
                                if (item.Model.IsInvalid)
                                    return (<div style={{ color: "red" }}>{item.Model.SaleProvider}</div>);
                                else
                                    return (<div>{item.Model.SaleProvider}</div>);
                            }
                        };

                    case "PatientName":
                        return {
                            renderCell: item => (
                                <div><HyperLinkRedirectControl target={RedirectTo.Patient} redirectValue={item.Model.PatientContactId} text={item.Model.PatientName} /></div>
                            )
                        };

                    case "NAM":
                        return {
                            renderCell: item => (
                                <div>{item.Model.patientNAM}</div>
                            )
                        };

                    case "PaymentMethod":
                        return {
                            renderCell: item => {
                                if (item.Model.IsInvalid)
                                    return (<div style={{ color: "red" }}>{item.Model.PaymentMethod}</div>);
                                else
                                    return (<div>{item.Model.PaymentMethod}</div>);
                            }
                        };

                    case "BilledBy":
                        return {
                            renderCell: item => {
                                if (item.Model.IsInvalid)
                                    return (<div style={{ color: "red" }}>{item.Model.BilledBy}</div>);
                                else
                                    return (<div>{item.Model.BilledBy}</div>);
                            }
                        };

                    case "ServiceID":
                        return {
                            renderCell: item => {
                                if (item.Model.IsInvalid)
                                    return (<div style={{ color: "red" }}>{item.Model.ServiceID}</div>);
                                else
                                    return (<div>{item.Model.ServiceID}</div>);
                            }
                        };

                    case "Description":
                        return {
                            renderCell: item => {
                                if (item.Model.IsInvalid)
                                    return (<div style={{ color: "red" }}>{item.Model.Description}</div>);
                                else
                                    return (<div>{item.Model.Description}</div>);
                            }
                        };
                    case "Quantity":
                        return {
                            renderCell: item => {
                                if (item.Model.IsInvalid)
                                    return (<div style={{ color: "red" }}>{item.Model.Quantity}</div>);
                                else
                                    return (<div>{item.Model.Quantity}</div>);
                            }
                        };

                    case "BilledTo":
                        return {
                            renderCell: item => {
                                if (!cacheStore.ClinicSettings.EnablePrivateBillRecipient) return;

                                return (
                                    <div style={{ color: item.Model.IsInvalid ? "red" : "" }}>
                                        {(item.Model.BilledTo == PrivateBill_BilledTo.ThirdParty
                                            ? `${item.Model.RecipientName}`
                                            : cacheStore.GetString("PatientLabel"))
                                        }
                                    </div>
                                );
                            }
                        };

                    case "Price":
                        return {
                            renderCell: item => {
                                if (item.Model.IsInvalid)
                                    return (<div style={{ color: "red" }}>{item.Model.Price.toFixed(2)}</div>);
                                else
                                    return (<div>{item.Model.Price.toFixed(2)}</div>);
                            }
                        };

                    case "GST":
                        return {
                            renderCell: item => {
                                if (item.Model.IsInvalid)
                                    return (<div style={{ color: "red" }}>{item.Model.GST.toFixed(2)}</div>);
                                else
                                    return (<div>{item.Model.GST.toFixed(2)}</div>);
                            }
                        };

                    case "QST":
                        return {
                            renderCell: item => {
                                if (item.Model.IsInvalid)
                                    return (<div style={{ color: "red" }}>{item.Model.QST.toFixed(2)}</div>);
                                else
                                    return (<div>{item.Model.QST.toFixed(2)}</div>);
                            }
                        };

                    case "HST":
                        return {
                            renderCell: item => {
                                if (item.Model.IsInvalid)
                                    return (<div style={{ color: "red" }}>{item.Model.HST.toFixed(2)}</div>);
                                else
                                    return (<div>{item.Model.HST.toFixed(2)}</div>);
                            }
                        };

                    case "GenericTax":
                        return {
                            renderCell: item => {
                                if (item.Model.IsInvalid)
                                    return (<div style={{ color: "red" }}>{item.Model.GenericTax.toFixed(2)}</div>);
                                else
                                    return (<div>{item.Model.GenericTax.toFixed(2)}</div>);
                            }
                        };

                    case "Total":
                        return {
                            renderCell: item => (
                                <div style={item.Model.IsInvalid ? { color: "red" } : {}}>{item.Model.Total.toFixed(2)}</div>
                            )
                        };
                }
            },
            false
        );
    }

    public loadTableRenderOptions(columnOptions: Immutable.List<MyleTableColumnOptions>): ITableRenderOptions {
        // Get The Render Options
        var defaultTablePluginOptions = {
            MinColWidth: 60,
            MinTableWidth: 980,
            Cols: columnOptions.filter(x => x.IsHidden === false).map(i => i.WidthPercentage),
            FirstColumnNoDrag: false,
            LastColumnNoDrag: true,
            Autoresize: true,
            OnColumnWidthChange: null
        };
        return Immutable.fromJS(defaultTablePluginOptions);
    }

    private async onPrintReport(tableColumns: Immutable.List<MyleTableColumn>): Promise<void> {
        const columnsToShow = tableColumns.filter(p => p.columnOptions.IsHidden === false).map(p => p.columnOptions.FieldName).toArray();

        await billingStore.BillingAction.PrintSalesReport(billingStore.SalesInboxModel, columnsToShow);
    }

    public componentDidMount(): void {
        $(window).trigger("resize");
    }

    public componentDidUpdate(): void {
        $(window).trigger("resize");
    }

    private shouldShowGst(): boolean {
        return this.props.Model.get("GSTSum") > 0
            || EnvironmentTax.hasGst();
    }

    private shouldShowPstOrQst(): boolean {
        return this.props.Model.get("QSTSum") > 0
            || EnvironmentTax.hasPstOrQst();
    }

    private shouldShowHst(): boolean {
        return this.props.Model.get("HSTSum") > 0
            || EnvironmentTax.hasHst();
    }

    private shouldShowGenericTax(): boolean {
        return this.props.Model.get("GenericTaxSum") > 0
            || EnvironmentTax.hasGenericTax();
    }
}

export { SalesInbox };
